import { WidgetType } from '@/api/rest/resources/types/widget';

import { IndicatorFactType } from './fact';

export enum IndicatorTiersEnum {
  free = 'free',
  add_on = 'add_on',
  lite = 'lite',
  pro = 'pro',
}

export type IndicatorTiersType = keyof typeof IndicatorTiersEnum;

export enum VerifiedStatusEnum {
  verified = 'verified',
  not_verified = 'not_verified',
  pending = 'pending',
}

export type VerifiedStatusType = keyof typeof VerifiedStatusEnum;

export enum VerifiedByEnum {
  third_party_verified = 'third_party_verified',
  self_verified = 'self_verified',
}

export type VerifiedByType = keyof typeof VerifiedByEnum;

export type IndicatorProduct = {
  id: string;
  purchased_at: Date | null;
  tier?: string;
  name: string;
  widget: {
    type: WidgetType;
    widget_format: {
      overview?: {
        title?: string;
        pill?: string;
      };
      data_sources?: string[];
      details?: {
        verification?: string;
        financial_modelling?: string;
        measurement_protocol?: string;
        definition_and_scientific_basis?: string;
      };
    };
  };
  verification_information?: {
    status?: string;
    verified_by?: string;
  };
  count_of_applied_assets?: number;
  show_calculation_progress?: boolean;
  is_recommended?: boolean;
  indicator_names: IndicatorFactType[];
};

export type IndicatorProductCalculationProgres = {
  indicator_product_id: string;
  expected_fact_count: number;
  completed_fact_count: number;
  remaining_fact_count: number;
  failed_fact_count: number;
};

export enum INDICATOR_PRODUCT_NAME {
  LANDSCAPE_PROTECTED_AREAS_IBAT = 'landscape_protected_areas_wdpa',
  LANDSCAPE_PROTECTED_AREAS_OSM = 'landscape_protected_areas_osm',
  LANDSCAPE_THREATENED_SPECIES = 'landscape_species_presence',
}

export const INDICATOR = {
  // BE defined Indicator Products
  CARBON: 'carbon', // below ground carbon
  ABOVE_GROUND_CARBON: 'above_ground_carbon',
  ESA_ABOVE_GROUND_CARBON: 'esa_above_ground_carbon',
  WATER: 'water', // water holding capacity
  PROTECTED_ON_FARM_HABITAT: 'protected_on_farm_habitat',
  LANDSCAPE_PROTECTED_AREAS_OSM: 'landscape_protected_areas_osm',
  LANDSCAPE_PROTECTED_AREAS_WDPA: 'landscape_protected_areas_wdpa',
  LANDSCAPE_SPECIES_PRESENCE: 'landscape_species_presence',
  FREE_LANDSCAPE_SPECIES: 'free_landscape_species',
  FREE_SOCIAL_LIFE_EXPECTANCY: 'free_social_life_expectancy',
  EO_VEGETATION_CONDITION: 'eo_vegetation_condition',
  FREE_CLIMATE_RISK: 'free_climate_risk',
  LANDSCAPE_LANDCOVER_LANDUSE: 'landscape_landcover_landuse',
  MONETARY_RISK: 'monetary_risk',
  DEFORESTATION: 'deforestation',
  COOLFARMTOOL_CO2_SPATIALISED: 'coolfarmtool_co2_spatialised',
  CLIMATE_RISK: 'climate_risk',
  HABITAT_INTACTNESS: 'habitat_intactness', // TODO: MVP-7540 check BE name
  ECOSYSTEM_INTEGRITY: 'ecosystem_integrity',

  // FE defined Indicator Products (some are part of other Indicator Products but the FE needs to handle them separately)
  CANOPY_COVER: 'canopy_cover',
  CANOPY_HEIGHT: 'canopy_height',
  PRECIPITATION: 'precipitation',
  SOIL_MOISTURE: 'soil_moisture',
  HABITAT_INTACTNESS_THREATS_BREAKDOWN: 'habitat_intactness_threats_breakdown',
  POPULATION_DENSITY: 'population_density',
  CLEANWAVE: 'cleanwave',
} as const;

/**
 * GET /api/v1/dashboard/indicator-product
 */
export const GET_INDICATOR_PRODUCTS_PATH = 'dashboard/indicator-product';
export type GetIndicatorProductsRequestPathVariables = undefined;
export type GetIndicatorProductsRequestQueryParams = {
  organization_id: string;
  purchased?: boolean;
  visible_for_customer?: boolean;
};
export type GetIndicatorProductsSuccessDto = IndicatorProduct[];
export type GetIndicatorProductsErrorDto = Error;

/**
 * POST /api/v1/dashboard/indicator-product/subscribe
 */
export const POST_INDICATOR_PRODUCT_SUBSCRIBE_PATH = 'dashboard/indicator-product/subscribe';
export type PostIndicatorProductSubscribeRequestPathVariables = undefined;
export type PostIndicatorProductSubscribeRequestQueryParams = undefined;
export type PostIndicatorProductSubscribeBodyData = { organization_id: string; indicator_product_ids: string[] };
export type PostIndicatorProductSubscribeSuccessDto = {
  subscriptions: {
    indicator_product_id: string;
    subscription_id: string;
    success: boolean;
    error: string;
  }[];
  count_of_applied_assets: number;
  total_subscriptions_created: number;
  total_subscriptions_failed: number;
};
export type PostIndicatorProductSubscribeErrorDto = Error;

/**
 * GET /api/v1/dashboard/indicator-product/calculation-progress
 */
export const GET_INDICATOR_PRODUCTS_CALCULATION_PROGRESS_PATH = 'dashboard/indicator-product/calculation-progress';
export type GetIndicatorProductsCalculationProgressRequestPathVariables = undefined;
export type GetIndicatorProductsCalculationProgressRequestQueryParams = {
  indicator_product_id: string;
  organization_id: string;
};
export type GetIndicatorProductsCalculationProgressSuccessDto = IndicatorProductCalculationProgres;
export type GetIndicatorProductsCalculationProgressErrorDto = Error;

/**
 * GET /api/v1/dashboard/co-pilot-config/{organization_id}/recommended-products/
 */
export const GET_ORGANIZATION_RECOMMENDED_INDICATOR_PRODUCTS_PATH =
  'dashboard/co-pilot-config/{organization_id}/recommended-products';
export type GetOrganizationRecommendedIndicatorProductsRequestPathVariables = { organization_id: string };
export type GetOrganizationRecommendedIndicatorProductsRequestQueryParams = undefined;
export type GetOrganizationRecommendedIndicatorProductsSuccessDto = IndicatorProduct[];
export type GetOrganizationRecommendedIndicatorProductsErrorDto = Error;
